import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { assetsApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { QrCode, Camera, ArrowRight, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';

export const QRScannerPage = () => {
  const [scanning, setScanning] = useState(false);
  const [scannedAsset, setScannedAsset] = useState(null);
  const [loading, setLoading] = useState(false);
  const scannerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    return () => {
      if (scannerRef.current) {
        try {
          scannerRef.current.clear();
        } catch (e) {
          console.log('Scanner cleanup error:', e);
        }
      }
    };
  }, []);

  const startScanner = () => {
    setScanning(true);
    setScannedAsset(null);

    setTimeout(() => {
      scannerRef.current = new Html5QrcodeScanner(
        "qr-reader",
        { 
          fps: 10, 
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0,
          showTorchButtonIfSupported: true,
          showZoomSliderIfSupported: true,
        },
        false
      );

      scannerRef.current.render(onScanSuccess, onScanError);
    }, 100);
  };

  const stopScanner = () => {
    if (scannerRef.current) {
      try {
        scannerRef.current.clear();
      } catch (e) {
        console.log('Stop scanner error:', e);
      }
    }
    setScanning(false);
  };

  const onScanSuccess = async (decodedText) => {
    setLoading(true);
    stopScanner();

    try {
      const response = await assetsApi.getByQR(decodedText);
      setScannedAsset(response.data);
      toast.success('Asset found!');
    } catch (error) {
      toast.error('Asset not found or invalid QR code');
      setScannedAsset(null);
    } finally {
      setLoading(false);
    }
  };

  const onScanError = (error) => {
    // Ignore frequent scan errors
    console.debug('QR scan error:', error);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-900/30 text-green-400 border-green-900';
      case 'in_maintenance': return 'bg-yellow-900/30 text-yellow-400 border-yellow-900';
      case 'out_of_service': return 'bg-red-900/30 text-red-400 border-red-900';
      default: return 'bg-gray-800 text-gray-400';
    }
  };

  return (
    <div className="max-w-lg mx-auto space-y-8 animate-fade-in" data-testid="qr-scanner-page">
      <div className="text-center">
        <h1 className="text-[20px] font-extrabold uppercase tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
          QR Scanner
        </h1>
        <p className="text-muted-foreground mt-1">Scan an asset QR code to view details or start inspection</p>
      </div>

      {!scanning && !scannedAsset && !loading && (
        <Card className="card-hover">
          <CardContent className="p-8 text-center">
            <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <QrCode className="w-12 h-12 text-primary" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Ready to Scan</h2>
            <p className="text-muted-foreground mb-6">
              Point your camera at an asset's QR code to quickly access it
            </p>
            <Button onClick={startScanner} className="btn-industrial" size="lg" data-testid="start-scan-btn">
              <Camera className="w-5 h-5 mr-2" />
              Start Scanning
            </Button>
          </CardContent>
        </Card>
      )}

      {scanning && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="uppercase tracking-wide">Scanning...</CardTitle>
              <Button variant="ghost" size="icon" onClick={stopScanner} data-testid="stop-scan-btn">
                <X className="w-5 h-5" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div 
              id="qr-reader" 
              className="overflow-hidden rounded-lg"
              style={{ 
                border: '3px solid hsl(48, 100%, 50%)',
                borderRadius: '12px'
              }}
            />
            <p className="text-center text-muted-foreground mt-4 text-sm">
              Position the QR code within the frame
            </p>
          </CardContent>
        </Card>
      )}

      {loading && (
        <Card>
          <CardContent className="p-8 text-center">
            <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Looking up asset...</p>
          </CardContent>
        </Card>
      )}

      {scannedAsset && (
        <Card className="border-primary">
          <CardHeader>
            <CardTitle className="uppercase tracking-wide flex items-center justify-between">
              Asset Found
              <Badge className={getStatusColor(scannedAsset.status)}>
                {scannedAsset.status.replace('_', ' ')}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h2 className="text-2xl font-bold">{scannedAsset.name}</h2>
              <p className="text-muted-foreground">{scannedAsset.serial_number}</p>
            </div>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Category</span>
                <p className="font-medium capitalize">{scannedAsset.category.replace('_', ' ')}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Make/Model</span>
                <p className="font-medium">{scannedAsset.make} {scannedAsset.model}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Year</span>
                <p className="font-medium">{scannedAsset.year}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Hours</span>
                <p className="font-medium">{scannedAsset.hours?.toLocaleString() || 0}</p>
              </div>
            </div>

            <div className="grid gap-3 pt-4">
              <Button 
                className="btn-industrial w-full" 
                onClick={() => navigate(`/inspections/new?asset=${scannedAsset.id}`)}
                data-testid="start-inspection-btn"
              >
                Start Inspection
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => navigate(`/assets/${scannedAsset.id}`)}
                data-testid="view-asset-btn"
              >
                View Full Details
              </Button>
              <Button 
                variant="ghost" 
                className="w-full"
                onClick={() => {
                  setScannedAsset(null);
                  startScanner();
                }}
                data-testid="scan-another-btn"
              >
                Scan Another
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
